import { useEffect, useRef, useState } from 'react';
import { useCameraStore } from '@/store/cameraStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useEngine } from '@/app/EngineContext';
import Paper from '@mui/material/Paper';
import styles from './Chrome.module.css';

/**
 * Zoom and pointer readouts update at pointer speed, so they are written to the DOM
 * directly instead of through React state.
 */
export const StatusBar = () => {
  const { events, frames } = useEngine();
  const objectCount = useWorkspaceStore((s) => Object.keys(s.workspaces[s.activeWorkspaceId]?.objects ?? {}).length);
  const selectedCount = useSelectionStore((s) => s.selection.objectIds.length + s.selection.connectionIds.length);
  const pointerRef = useRef<HTMLElement>(null);
  const [zoomText, setZoomText] = useState(() => `${Math.round(useCameraStore.getState().camera.zoom * 100)}%`);

  useEffect(() => {
    const unsubCamera = useCameraStore.subscribe((s, prev) => {
      if (s.camera.zoom !== prev.camera.zoom)
        frames.schedule(() => {
          setZoomText(`${Math.round(s.camera.zoom * 100)}%`);
        });
    });
    const unsubPointer = events.on('pointer:world', (e) => {
      if (pointerRef.current) pointerRef.current.textContent = `${Math.round(e.world.x)}, ${Math.round(e.world.y)}`;
    });
    return () => {
      unsubCamera();
      unsubPointer();
    };
  }, [events, frames]);

  return (
    <Paper component="footer" className={styles.statusbar} aria-label="Status" role="status">
      <span>
        zoom <b data-testid="zoom-readout">{zoomText}</b>
      </span>
      <span>
        world <b ref={pointerRef}>–, –</b>
      </span>
      <span>
        objects <b data-testid="object-count">{objectCount}</b>
      </span>
      <span>
        selected <b>{selectedCount}</b>
      </span>
    </Paper>
  );
};
